import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Lock, Mail, User as UserIcon, Building2, Phone, Loader2, KeyRound, Eye, EyeOff } from "lucide-react";

/**
 * Account page — area riservata utente.
 *
 * Visibile a tutti gli utenti loggati (non admin). Permette di:
 *  - visualizzare i propri dati anagrafici (read-only)
 *  - cambiare la password fornendo quella attuale come conferma
 *
 * I dati anagrafici non sono modificabili dall'utente: per modifiche
 * deve contattare l'admin (vincolo di compliance / audit).
 */
export default function Account() {
  const { user } = useAuth();
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!user) {
    return <div className="max-w-3xl mx-auto p-8 text-sm text-slate1">Caricamento...</div>;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) {
      toast.error("La nuova password deve avere almeno 6 caratteri");
      return;
    }
    if (newPw !== newPw2) {
      toast.error("Le due nuove password non coincidono");
      return;
    }
    if (newPw === currentPw) {
      toast.error("La nuova password deve essere diversa da quella attuale");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPw,
        new_password: newPw,
      });
      toast.success("Password aggiornata con successo");
      setCurrentPw("");
      setNewPw("");
      setNewPw2("");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Errore aggiornamento password";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const accountTypeLabel = (
    user.account_type === "trader" ? "Trader / Rivenditore" :
    user.account_type === "real_estate" ? "Real Estate / Agenzia" :
    "Studio Architetto"
  );

  return (
    <div className="max-w-3xl mx-auto p-6 lg:p-8 space-y-8" data-testid="account-page">
      <div>
        <div className="text-[11px] tracking-[0.2em] uppercase text-slate1">Area riservata</div>
        <h1 className="font-display text-3xl text-ink mt-1">Il tuo account</h1>
        <p className="text-sm text-slate1 mt-2">
          Visualizza i tuoi dati e gestisci la sicurezza dell&apos;account. Per modifiche all&apos;anagrafica, contatta l&apos;amministratore.
        </p>
      </div>

      {/* Dati anagrafici (read-only) */}
      <section className="bg-white border border-ink/10 rounded-sm p-6" data-testid="account-data">
        <div className="text-[11px] tracking-[0.2em] uppercase text-slate1 mb-4 inline-flex items-center gap-2">
          <UserIcon size={12} /> Dati profilo
        </div>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8">
          <Field icon={UserIcon} label="Nome">{user.name || "—"}</Field>
          <Field icon={Mail} label="Email">{user.email}</Field>
          <Field icon={Building2} label="Studio / Azienda">{user.studio || "—"}</Field>
          <Field icon={Phone} label="Telefono">{user.phone || "—"}</Field>
          <Field icon={UserIcon} label="Tipo account">
            <span className="inline-block px-2 py-0.5 bg-cream2 border border-ink/15 rounded-sm text-xs">{accountTypeLabel}</span>
          </Field>
          <Field icon={KeyRound} label="Wallet">
            € {(user.wallet_credit || 0).toFixed(2)}
          </Field>
        </dl>
      </section>

      {/* Cambio password */}
      <section className="bg-white border border-ink/10 rounded-sm p-6" data-testid="account-change-password">
        <div className="text-[11px] tracking-[0.2em] uppercase text-slate1 mb-1 inline-flex items-center gap-2">
          <Lock size={12} /> Sicurezza
        </div>
        <h2 className="font-display text-xl text-ink">Cambia password</h2>
        <p className="text-sm text-slate1 mt-1 mb-5">
          Per confermare l&apos;operazione devi fornire la password attuale.
        </p>
        <form onSubmit={submit} className="space-y-4 max-w-md">
          {/* Current password */}
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase text-slate1 mb-1.5">Password attuale</label>
            <div className="relative">
              <input
                type={showCurrent ? "text" : "password"}
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full px-3 py-2 pr-10 border border-ink/15 rounded-sm bg-white text-sm focus:outline-none focus:border-gold"
                data-testid="account-current-password"
              />
              <button
                type="button"
                onClick={() => setShowCurrent((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate1 hover:text-ink"
                tabIndex={-1}
              >
                {showCurrent ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>

          {/* New password */}
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase text-slate1 mb-1.5">Nuova password (min 6 caratteri)</label>
            <div className="relative">
              <input
                type={showNew ? "text" : "password"}
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                required
                minLength={6}
                autoComplete="new-password"
                className="w-full px-3 py-2 pr-10 border border-ink/15 rounded-sm bg-white text-sm focus:outline-none focus:border-gold"
                data-testid="account-new-password"
              />
              <button
                type="button"
                onClick={() => setShowNew((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate1 hover:text-ink"
                tabIndex={-1}
              >
                {showNew ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>

          {/* Confirm */}
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase text-slate1 mb-1.5">Conferma nuova password</label>
            <input
              type={showNew ? "text" : "password"}
              value={newPw2}
              onChange={(e) => setNewPw2(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
              className="w-full px-3 py-2 border border-ink/15 rounded-sm bg-white text-sm focus:outline-none focus:border-gold"
              data-testid="account-new-password-2"
            />
            {newPw && newPw2 && newPw !== newPw2 && (
              <div className="text-[11px] text-rose-700 mt-1">Le password non coincidono</div>
            )}
          </div>

          <button
            type="submit"
            disabled={saving || !currentPw || !newPw || !newPw2}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-ink text-cream text-[12px] tracking-[0.2em] uppercase rounded-sm hover:bg-ink/90 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="account-save-password"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
            {saving ? "Salvataggio..." : "Aggiorna password"}
          </button>
        </form>
      </section>
    </div>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <div>
      <dt className="text-[10px] tracking-[0.2em] uppercase text-slate1 mb-1 inline-flex items-center gap-1.5">
        {Icon && <Icon size={11} />} {label}
      </dt>
      <dd className="text-sm text-ink">{children}</dd>
    </div>
  );
}
